import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MaterialUiDivider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import ArchiveIcon from "@material-ui/icons/Archive";
import AssignmentIcon from "@material-ui/icons/Assignment";
import AssignmentLateIcon from "@material-ui/icons/AssignmentLate";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import PlaceIcon from "@material-ui/icons/Place";
import UnarchiveIcon from "@material-ui/icons/Unarchive";
import { withSnackbar } from "notistack";
import React, { useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import Divisions from "./Divisions";
import General from "./General";
import Location from "./Location";
import Notes from "./Notes";
import PatrolWindows from "./PatrolWindows";
import VIP from "./VIP";
import AlertDialog from "../Alert";
import LoadingButton from "../../Buttons/Loading";
import * as appsyncOperations from "../../../utils/appsyncOperations";
import * as actions from "../../../store/actions";
import PropertyTimeline from "../../Calendars/PropertyTimeline";
import { isUserRole } from "../../../utils/user";
import findDivisionRelations from "../../../utils/findDivisionRelations";
import getPropertyMarker from "../../../utils/getPropertyMarker";
import getAppUser from "../../../store/selectors/getAppUser";
import requestResponse from "../../../utils/requestResponse";
import CreateDar from "../../Dialogs/CreateDar";
import CreateIncidentReport from "../../Dialogs/CreateIncidentReport";
import * as colors from "../../../styles/colors";

const PropertyDialog = ({
  isOpen,
  onClose,
  property,
  dispatch,
  history,
  appUser,
  enqueueSnackbar,
}) => {
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    content: "",
    onYes: () => null,
    onNo: () => null,
    onClose: () => null,
  });
  const [editMode, setEditMode] = useState({
    enabled: false,
    submitPending: false,
    onSubmit: () => null,
    onCancel: () => null,
  });
  const [selectedContent, setSelectedContent] = useState(content.GENERAL);

  const handleContentChange = (content) => {
    setSelectedContent(content);
  };
  const enableEditor = (onSubmit, onCancel) => {
    setEditMode({
      enabled: true,
      onSubmit: onSubmit,
      onCancel: (property) => {
        setEditMode({
          enabled: false,
          submitPending: false,
          onSubmit: () => null,
          onCancel: () => null,
        });
        onCancel(property);
      },
    });
  };
  const updateProperty = async (input, onSuccess) => {
    setEditMode((prevState) => ({
      ...prevState,
      submitPending: true,
    }));
    try {
      const updatedProperty = await appsyncOperations.updateProperty(input);
      setEditMode({
        enabled: false,
        submitPending: false,
        onSubmit: () => null,
        onCancel: () => null,
      });
      onSuccess(updatedProperty);
      enqueueSnackbar("Edit saved!", { variant: "success" });
      dispatch(actions.properties.setProperty(updatedProperty));
    } catch (error) {
      setEditMode((prevState) => ({
        ...prevState,
        submitPending: false,
      }));
      if (typeof error === "string") {
        enqueueSnackbar(error, { variant: "error" });
      } else {
        enqueueSnackbar("An unexpected error occurred.", { variant: "error" });
        console.warn(error);
      }
    }
  };
  const updatePropertyDivisions = async (input, onSuccess) => {
    setEditMode((prevState) => ({
      ...prevState,
      submitPending: true,
    }));
    try {
      const updatedDivisionRelations = await appsyncOperations.batchWriteDivisionRelations(
        input
      );
      setEditMode({
        enabled: false,
        submitPending: false,
        onSubmit: () => null,
        onCancel: () => null,
      });
      dispatch(
        actions.divisions.setDivisions({
          divisionRelations: updatedDivisionRelations,
        })
      );
      onSuccess(
        Object.keys(findDivisionRelations(property.propertyId).included)
      );
      enqueueSnackbar("Edit saved!", { variant: "success" });
    } catch (error) {
      setEditMode((prevState) => ({
        ...prevState,
        submitPending: false,
      }));
      if (typeof error === "string") {
        enqueueSnackbar(error, { variant: "error" });
      } else {
        enqueueSnackbar("An unexpected error occurred.", { variant: "error" });
        console.warn(error);
      }
    }
  };

  const [showCreateDar, setShowCreateDar] = useState(false);
  const [showCreateIncident, setShowCreateIncident] = useState(false);
  const [marker] = useState(() => getPropertyMarker(property).markerParams);
  const [responseLoading, setResponseLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const onRequestResponse = async () => {
    setResponseLoading(true);
    try {
      await requestResponse({
        property,
        appUser,
        navigate: (property) => {
          const { propertyId, instanceId } = property;
          history.push(`/map/?property=${propertyId}&response=${instanceId}`);
        },
      });
    } catch (e) {
      enqueueSnackbar(e, { variant: "error" });
      console.error(e);
    }
    setResponseLoading(false);
  };

  const onDeleteChange = async () => {
    if (!property.isDeleted) {
      setAlertDialog({
        open: true,
        content: "Are you sure you want to archive this property?",
        onYes: async () => {
          setAlertDialog((prevState) => ({
            ...prevState,
            open: false,
          }));
          setDeleteLoading(true);
          try {
            const updatedProperty = await appsyncOperations.updateProperty({
              propertyId: property.propertyId,
              isDeleted: true,
            });
            dispatch(actions.properties.setProperty(updatedProperty));
          } catch (error) {
            if (typeof error === "string") {
              enqueueSnackbar(error, { variant: "error" });
            } else {
              enqueueSnackbar("An unexpected error occurred.", {
                variant: "error",
              });
              console.warn(error);
            }
          }
          setDeleteLoading(false);
        },
        onNo: () => {
          setAlertDialog((prevState) => ({ ...prevState, open: false }));
        },
        onClose: () => {
          setAlertDialog((prevState) => ({ ...prevState, open: false }));
        },
      });
    } else {
      setDeleteLoading(true);
      try {
        const updatedProperty = await appsyncOperations.updateProperty({
          propertyId: property.propertyId,
          isDeleted: null,
        });
        dispatch(actions.properties.setProperty(updatedProperty));
      } catch (error) {
        if (typeof error === "string") {
          enqueueSnackbar(error, { variant: "error" });
        } else {
          enqueueSnackbar("An unexpected error occurred.", {
            variant: "error",
          });
          console.warn(error);
        }
      }
      setDeleteLoading(false);
    }
  };

  const isAdmin = isUserRole({ admin: true });
  const updatePending =
    deleteLoading || responseLoading || editMode.submitPending;
  const classes = useStyles();

  return (
    property && (
      <>
        <Dialog
          open={isOpen}
          onClose={onClose}
          fullWidth
          maxWidth="lg"
          scroll="paper"
        >
          <DialogTitle>Property Details</DialogTitle>
          <div
            style={{ ...styles.statusBanner, backgroundColor: marker.color }}
          >
            <Typography>{marker.reason}</Typography>
            {property.instanceId && (
              <>
                <div className={classes.bannerTextButtonSeparator} />
                <Button
                  onClick={() =>
                    history.push(
                      `/map?property=${property.propertyId}&response=${property.instanceId}`
                    )
                  }
                  classes={{
                    root: classes.responseButtonRoot,
                    outlined: classes.responseButtonOutlined,
                  }}
                  disabled={editMode.enabled || updatePending}
                  variant="outlined"
                >
                  View Messages
                </Button>
              </>
            )}
          </div>
          <DialogContent classes={{ root: classes.dialogContent }}>
            <div className={classes.contentContainer}>
              <div className={classes.drawer}>
                <List>
                  <Button
                    classes={{ label: classes.buttonLabel }}
                    onClick={() =>
                      history.push(`/map?property=${property.propertyId}`)
                    }
                    color="primary"
                    disabled={
                      property.isDeleted || editMode.enabled || updatePending
                    }
                    fullWidth
                    startIcon={<PlaceIcon />}
                  >
                    Locate
                  </Button>
                  <Button
                    classes={{ label: classes.buttonLabel }}
                    onClick={onRequestResponse}
                    color="primary"
                    disabled={
                      property.isDeleted ||
                      property.instanceId ||
                      editMode.enabled ||
                      updatePending
                    }
                    fullWidth
                    startIcon={
                      responseLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <NotificationsActiveIcon />
                      )
                    }
                  >
                    Request Response
                  </Button>
                  <Button
                    classes={{ label: classes.buttonLabel }}
                    onClick={() => setShowCreateDar(true)}
                    color="primary"
                    disabled={
                      property.isDeleted || editMode.enabled || updatePending
                    }
                    fullWidth
                    startIcon={<AssignmentIcon />}
                  >
                    New DAR
                  </Button>
                  <Button
                    classes={{ label: classes.buttonLabel }}
                    onClick={() => setShowCreateIncident(true)}
                    color="primary"
                    disabled={
                      property.isDeleted || editMode.enabled || updatePending
                    }
                    fullWidth
                    startIcon={<AssignmentLateIcon />}
                  >
                    New Incident Report
                  </Button>
                  {!property.isDeleted ? (
                    <Button
                      classes={{ label: classes.buttonLabel }}
                      onClick={onDeleteChange}
                      color="primary"
                      disabled={editMode.enabled || updatePending || !isAdmin}
                      fullWidth
                      startIcon={
                        deleteLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <ArchiveIcon />
                        )
                      }
                    >
                      Archive
                    </Button>
                  ) : (
                    <Button
                      classes={{ label: classes.buttonLabel }}
                      onClick={onDeleteChange}
                      color="primary"
                      disabled={editMode.enabled || updatePending || !isAdmin}
                      fullWidth
                      startIcon={
                        deleteLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <UnarchiveIcon />
                        )
                      }
                    >
                      Restore
                    </Button>
                  )}
                </List>
                <MaterialUiDivider />
                <List>
                  <ListSubheader
                    classes={{ gutters: classes.listItemGutters }}
                    disableSticky
                  >
                    Settings
                  </ListSubheader>
                  <ListItem
                    classes={{ gutters: classes.listItemGutters }}
                    onClick={() => handleContentChange(content.GENERAL)}
                    button
                    dense
                    disabled={editMode.enabled || editMode.submitPending}
                    selected={selectedContent === content.GENERAL}
                  >
                    <ListItemText>General</ListItemText>
                  </ListItem>
                  <ListItem
                    classes={{ gutters: classes.listItemGutters }}
                    onClick={() => handleContentChange(content.DIVISIONS)}
                    button
                    dense
                    disabled={editMode.enabled || editMode.submitPending}
                    selected={selectedContent === content.DIVISIONS}
                  >
                    <ListItemText>Divisions</ListItemText>
                  </ListItem>
                  <ListItem
                    classes={{ gutters: classes.listItemGutters }}
                    onClick={() => handleContentChange(content.LOCATION)}
                    button
                    dense
                    disabled={editMode.enabled || editMode.submitPending}
                    selected={selectedContent === content.LOCATION}
                  >
                    <ListItemText>Location</ListItemText>
                  </ListItem>
                  <ListItem
                    classes={{ gutters: classes.listItemGutters }}
                    onClick={() => handleContentChange(content.NOTES)}
                    button
                    dense
                    disabled={editMode.enabled || editMode.submitPending}
                    selected={selectedContent === content.NOTES}
                  >
                    <ListItemText>Notes</ListItemText>
                  </ListItem>
                  <ListItem
                    classes={{ gutters: classes.listItemGutters }}
                    onClick={() => handleContentChange(content.PATROL_WINDOWS)}
                    button
                    dense
                    disabled={editMode.enabled || editMode.submitPending}
                    selected={selectedContent === content.PATROL_WINDOWS}
                  >
                    <ListItemText>Patrol Windows</ListItemText>
                  </ListItem>
                  <ListItem
                    classes={{ gutters: classes.listItemGutters }}
                    onClick={() => handleContentChange(content.VIP)}
                    button
                    dense
                    disabled={editMode.enabled || editMode.submitPending}
                    selected={selectedContent === content.VIP}
                  >
                    <ListItemText>VIP</ListItemText>
                  </ListItem>
                </List>
                <MaterialUiDivider />
                <List>
                  <ListItem
                    classes={{ gutters: classes.listItemGutters }}
                    onClick={() => handleContentChange(content.TIMELINE)}
                    button
                    dense
                    disabled={editMode.enabled || editMode.submitPending}
                    selected={selectedContent === content.TIMELINE}
                  >
                    <ListItemText>Timeline</ListItemText>
                  </ListItem>
                </List>
              </div>
              <div className={classes.content}>
                {selectedContent === content.GENERAL && (
                  <General
                    property={property}
                    admin={isAdmin}
                    disabled={updatePending}
                    onEdit={enableEditor}
                    onSubmit={updateProperty}
                  />
                )}
                {selectedContent === content.LOCATION && (
                  <Location
                    property={property}
                    admin={isAdmin}
                    disabled={updatePending}
                    onEdit={enableEditor}
                    onSubmit={updateProperty}
                  />
                )}
                {selectedContent === content.VIP && (
                  <VIP
                    property={property}
                    disabled={updatePending}
                    onEdit={enableEditor}
                    onSubmit={updateProperty}
                  />
                )}
                {selectedContent === content.NOTES && (
                  <Notes
                    property={property}
                    disabled={updatePending}
                    onEdit={enableEditor}
                    onSubmit={updateProperty}
                  />
                )}
                {selectedContent === content.PATROL_WINDOWS && (
                  <PatrolWindows
                    property={property}
                    disabled={updatePending}
                    onEdit={enableEditor}
                    onSubmit={updateProperty}
                  />
                )}
                {selectedContent === content.DIVISIONS && (
                  <Divisions
                    property={property}
                    disabled={updatePending}
                    onEdit={enableEditor}
                    onSubmit={updatePropertyDivisions}
                  />
                )}
                {selectedContent === content.TIMELINE && (
                  <PropertyTimeline propertyId={property.propertyId} />
                )}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            {!editMode.enabled ? (
              <Button onClick={onClose} color="primary">
                Close
              </Button>
            ) : (
              <>
                <LoadingButton
                  color="primary"
                  onClick={editMode.onSubmit}
                  isLoading={editMode.submitPending}
                >
                  Save
                </LoadingButton>
                <Button
                  onClick={() => editMode.onCancel(property)}
                  color="primary"
                >
                  Cancel
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
        <CreateDar
          isOpen={showCreateDar}
          onClose={() => setShowCreateDar(false)}
          propertyId={property.propertyId}
        />
        <CreateIncidentReport
          isOpen={showCreateIncident}
          onClose={() => setShowCreateIncident(false)}
          propertyId={property.propertyId}
        />
        <AlertDialog
          content={alertDialog.content}
          isOpen={alertDialog.open}
          onYes={() => alertDialog.onYes(property)}
          onNo={alertDialog.onNo}
          onClose={alertDialog.onClose}
        />
      </>
    )
  );
};

const content = {
  GENERAL: "GENERAL",
  LOCATION: "LOCATION",
  VIP: "VIP",
  NOTES: "NOTES",
  PATROL_WINDOWS: "PATROL_WINDOWS",
  DIVISIONS: "DIVISIONS",
  TIMELINE: "TIMELINE",
};

const styles = {
  statusBanner: {
    width: "100%",
    height: 32,
    color: "white",
    padding: 24,
    alignItems: "center",
    display: "flex",
    marginBottom: 8,
  },
};

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: 0,
  },
  contentContainer: {
    display: "flex",
  },
  content: {
    flex: 1,
    paddingBottom: 8,
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 8,
  },
  drawer: {
    width: 240,
    flexShrink: 0,
  },
  bannerTextButtonSeparator: {
    width: 16,
  },
  buttonLabel: {
    justifyContent: "flex-start",
    paddingLeft: 16,
  },
  responseButtonRoot: {
    color: colors.WHITE,
  },
  responseButtonOutlined: {
    border: `1px solid ${colors.WHITE}`,
  },
  listItemGutters: {
    paddingLeft: 24,
    paddingRight: 24,
  },
  bannerContainer: {
    marginBottom: 24,
  },
  bannerContent: {
    height: 72,
    display: "flex",
  },
  bannerText: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    marginLeft: 16,
    marginRight: 16,
    marginTop: 16,
  },
}));

const mapStateToProps = (state) => ({
  appUser: getAppUser(state),
  serviceProviderId: state.auth.user.attributes["custom:service_provider_id"],
  divisions: state.divisions.byId,
});

export default withSnackbar(
  withRouter(connect(mapStateToProps)(PropertyDialog))
);
